import * as THREE from 'three'
import { Heightmap, smoothstep } from './heightmap'
import { Perlin } from '../core/noise'

/** Paleta po pojasevima — bira se prema visini i nagibu. */
const BOJE = {
  dubina: new THREE.Color('#12303f'),
  plicak: new THREE.Color('#2e6a6f'),
  pesak: new THREE.Color('#cdb47c'),
  livada: new THREE.Color('#527a37'),
  suma: new THREE.Color('#25452a'),
  suva: new THREE.Color('#8d7b3d'),
  kamen: new THREE.Color('#6b5c46'),
  litica: new THREE.Color('#473e34'),
  sneg: new THREE.Color('#eaf1f6'),
}

export interface TerrainResult {
  mesh: THREE.Mesh
  /** Tekstura dubine vode (R kanal, 0 = obala, 1 = duboko) za shader vode. */
  depthTexture: THREE.DataTexture
  dispose(): void
}

export function buildTerrain(hm: Heightmap): TerrainResult {
  const n = hm.res + 1
  const count = n * n

  const positions = new Float32Array(count * 3)
  const colors = new Float32Array(count * 3)
  const uvs = new Float32Array(count * 2)
  const indices = new Uint32Array(hm.res * hm.res * 6)

  const mrlja = new Perlin(hm.seed ^ 0x5bf03635)
  const sume = new Perlin(hm.seed ^ 0x2545f491)
  const c = new THREE.Color()
  const pomocna = new THREE.Color()

  // Sneg traži i apsolutnu visinu — na ravničarskoj mapi najviši vrh
  // nije planina, pa tamo snega uopšte nema.
  const evropa = hm.vrsta === 'evropa'
  const snegOd = evropa
    ? Math.max(150, hm.maxY * 0.86)
    : Math.max(200, hm.maxY * (hm.arhetip === 'planinska' ? 0.8 : 0.9))
  const imaSnega = hm.maxY > (evropa ? 155 : 210)
  const half = hm.size / 2

  for (let j = 0; j < n; j++) {
    for (let i = 0; i < n; i++) {
      const k = j * n + i
      const y = hm.heights[k]
      const x = -half + i * hm.cell
      const z = -half + j * hm.cell

      positions[k * 3] = x
      positions[k * 3 + 1] = y
      positions[k * 3 + 2] = z
      uvs[k * 2] = i / hm.res
      uvs[k * 2 + 1] = j / hm.res

      const nagib = hm.slopeAt(i, j)
      // Blaga varijacija boje da pojasevi ne budu pravilne trake.
      const varijacija = mrlja.fbm(i * 0.035, j * 0.035, 3) * 0.5 + 0.5

      if (y < 0) {
        c.copy(BOJE.dubina).lerp(BOJE.plicak, smoothstep(-hm.maxY * 0.16, -1.5, y))
      } else {
        // Pojasevi idu po tipičnoj visini kopna, ne po najvišem vrhu.
        const visinaT = y / hm.refVisina + (varijacija - 0.5) * 0.16
        c.copy(BOJE.livada)

        // Šume — krupne mrlje tamnijeg zelenila po nižim, blažim padinama.
        const sumaN = sume.fbm(i * 0.012, j * 0.012, 4) * 0.5 + 0.5
        const sumaUdeo =
          smoothstep(0.46, 0.64, sumaN) *
          (1 - smoothstep(0.5, 0.85, visinaT)) *
          (1 - smoothstep(0.3, 0.55, nagib))
        c.lerp(BOJE.suma, sumaUdeo * 0.9)

        // Suvoća kraja (Evropa) pomera boju ka žutoj i pre nego što se digne visina.
        const suvoca = hm.biom ? hm.biom[k] : 0
        c.lerp(BOJE.suva, Math.max(smoothstep(0.54, 0.94, visinaT), suvoca * 0.72))
        c.lerp(BOJE.kamen, smoothstep(0.86, 1.24, visinaT))

        // Plaža je uzana i vezana za apsolutnu visinu iznad mora.
        pomocna.copy(BOJE.pesak)
        c.lerp(pomocna, 1 - smoothstep(1.5, 9, y))

        // Sneg samo tamo gde može da se zadrži — strme litice ostaju gole.
        if (imaSnega) {
          const sneg =
            smoothstep(snegOd, snegOd + hm.maxY * 0.26, y) * (1 - smoothstep(0.34, 0.62, nagib))
          c.lerp(BOJE.sneg, sneg)
        }

        // Strmina uvek probija kao gola stena.
        pomocna.copy(BOJE.litica)
        c.lerp(pomocna, smoothstep(0.36, 0.72, nagib) * 0.9)
      }

      const svetlina = (0.9 + varijacija * 0.2) * hm.ao[k]
      colors[k * 3] = c.r * svetlina
      colors[k * 3 + 1] = c.g * svetlina
      colors[k * 3 + 2] = c.b * svetlina
    }
  }

  let t = 0
  for (let j = 0; j < hm.res; j++) {
    for (let i = 0; i < hm.res; i++) {
      const a = j * n + i
      const b = a + 1
      const d = a + n
      const e = d + 1
      indices[t++] = a
      indices[t++] = d
      indices[t++] = b
      indices[t++] = b
      indices[t++] = d
      indices[t++] = e
    }
  }

  const geom = new THREE.BufferGeometry()
  geom.setAttribute('position', new THREE.BufferAttribute(positions, 3))
  geom.setAttribute('color', new THREE.BufferAttribute(colors, 3))
  geom.setAttribute('uv', new THREE.BufferAttribute(uvs, 2))
  geom.setIndex(new THREE.BufferAttribute(indices, 1))
  geom.computeVertexNormals()
  geom.computeBoundingSphere()

  const mat = new THREE.MeshStandardMaterial({
    vertexColors: true,
    roughness: 0.94,
    metalness: 0.0,
  })
  dodajIzohipse(mat, hm.maxY)

  const mesh = new THREE.Mesh(geom, mat)
  mesh.castShadow = true
  mesh.receiveShadow = true
  mesh.name = 'teren'

  return {
    mesh,
    depthTexture: napraviTeksturuDubine(hm),
    dispose() {
      geom.dispose()
      mat.dispose()
    },
  }
}

/**
 * Tanke izohipse ucrtane u sam teren. Nose informaciju o visini
 * i bez okretanja kamere — mapa se čita kao štabna karta.
 */
function dodajIzohipse(mat: THREE.MeshStandardMaterial, maxY: number): void {
  const razmak = Math.max(20, Math.round(maxY / 12))
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uRazmak = { value: razmak }
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vSvetPoz;')
      .replace(
        '#include <begin_vertex>',
        '#include <begin_vertex>\nvSvetPoz = ( modelMatrix * vec4( transformed, 1.0 ) ).xyz;',
      )
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        '#include <common>\nvarying vec3 vSvetPoz;\nuniform float uRazmak;',
      )
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>
        {
          float nivo = vSvetPoz.y / uRazmak;
          float f = fract( nivo );
          float d = min( f, 1.0 - f );
          float w = fwidth( nivo ) * 1.1;
          float linija = 1.0 - smoothstep( 0.0, max( w, 0.0008 ), d );
          // Bez izohipsi pod vodom i na skoro ravnom terenu gde bi se razlile.
          linija *= smoothstep( 0.0, 6.0, vSvetPoz.y );
          linija *= 1.0 - smoothstep( 0.5, 1.4, w );
          diffuseColor.rgb *= 1.0 - 0.22 * linija;
        }`,
      )
  }
  mat.customProgramCacheKey = () => 'teren-izohipse'
}

/** Dubina vode spakovana u bajt teksturu — radi svuda, bez float ekstenzija. */
function napraviTeksturuDubine(hm: Heightmap): THREE.DataTexture {
  const n = hm.res + 1
  const data = new Uint8Array(n * n * 4)
  const maxDubina = Math.max(8, hm.maxY * 0.14)
  for (let k = 0; k < n * n; k++) {
    const y = hm.heights[k]
    const dubina = Math.min(1, Math.max(0, -y / maxDubina))
    const v = Math.round(dubina * 255)
    data[k * 4] = v
    data[k * 4 + 1] = v
    data[k * 4 + 2] = v
    data[k * 4 + 3] = 255
  }
  const tex = new THREE.DataTexture(data, n, n, THREE.RGBAFormat)
  tex.minFilter = THREE.LinearFilter
  tex.magFilter = THREE.LinearFilter
  tex.wrapS = THREE.ClampToEdgeWrapping
  tex.wrapT = THREE.ClampToEdgeWrapping
  tex.needsUpdate = true
  return tex
}
